import cn from "classnames";
import { isRangeMode } from "../../utils";
import { NavigationIcon } from "../navigation-icon";
import { hasDateLimit } from "../utils";
import { getWeeksOfMonth, isSameDates, isSameMonths } from "./utils";
import { WEEKDAYS, MONTHS_NAMES } from "./constants";
import { Day, DaysProps } from "./types";
import styles from "./styles.module.scss";

export const Days = ({
  minDate,
  maxDate,
  viewDate,
  selectedDate,
  dayRefs,
  onSelectDay,
  showPrevMonth,
  showNextMonth,
  showMonthsPanel,
  showYearsPanel,
  dataTestId,
}: DaysProps) => {
  const today = new Date();
  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();
  const monthWeeks = getWeeksOfMonth(viewYear, viewMonth);

  const viewMonthIsSelected = isRangeMode(selectedDate)
    ? selectedDate.some((date) => date && isSameMonths(viewDate, date))
    : selectedDate && isSameMonths(viewDate, selectedDate);

  const handleSelectDay = (day: Day) => () => {
    if (day) {
      onSelectDay(day);
    }
  };

  const isCurrentDay = (day: Day) => {
    if (day && isSameMonths(today, viewDate)) {
      const checkedDate = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        day
      );

      return isSameDates(checkedDate, today);
    }

    return false;
  };

  const isSelectedDay = (day: Day) => {
    if (day && viewMonthIsSelected) {
      const checkedDate = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        day
      );

      return isRangeMode(selectedDate)
        ? selectedDate.some((date) => date && isSameDates(date, checkedDate))
        : selectedDate && isSameDates(selectedDate, checkedDate);
    }

    return false;
  };

  const isDisabledDay = (day: Day) => {
    if (day) {
      const checkedDate = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        day
      );

      return hasDateLimit(checkedDate, minDate, maxDate);
    }

    return false;
  };

  const getRangeStatusOfDay = (day: Day) => {
    if (day && isRangeMode(selectedDate)) {
      const [startDay, endDay] = selectedDate;
      const isFilledRange = startDay && endDay;
      const isOneDayRange = isFilledRange && isSameDates(startDay, endDay);
      const checkedDate = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        day
      );

      return {
        [styles.starting]:
          isFilledRange && !isOneDayRange && isSameDates(startDay, checkedDate),
        [styles.ranging]:
          startDay && startDay < checkedDate && endDay && endDay > checkedDate,
        [styles.ending]:
          isFilledRange && !isOneDayRange && isSameDates(endDay, checkedDate),
      };
    }

    return {};
  };

  return (
    <div className={styles.panel} data-test-id={dataTestId}>
      <header className={styles.header}>
        <NavigationIcon
          direction="left"
          onClick={showPrevMonth}
          dataTestId={`${dataTestId}-prev-month`}
        />
        <p className={styles.title}>
          <button
            type="button"
            className={styles["select-month"]}
            onClick={showMonthsPanel}
            data-test-id={`${dataTestId}-select-month`}
          >
            {MONTHS_NAMES[viewMonth]}
          </button>
          <span className={styles.delimeter} />
          <button
            type="button"
            className={styles["select-year"]}
            onClick={showYearsPanel}
            data-test-id={`${dataTestId}-select-year`}
          >
            {viewYear}
          </button>
        </p>
        <NavigationIcon
          direction="right"
          onClick={showNextMonth}
          dataTestId={`${dataTestId}-next-month`}
        />
      </header>
      <table className={styles.dates}>
        <thead className={styles.weekdays}>
          <tr>
            {WEEKDAYS.map((weekday) => (
              <th className={styles.weekday} key={weekday}>
                {weekday}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.weeks}>
          {monthWeeks.map((week, i) => (
            <tr key={`week-${i}`}>
              {week.map((day, k) => {
                // необходимо для корректного определения, что нажатие было произведено именно по кнопке с датой
                // таким образом мы можем предотвратить смещение лейбла на промежуток времени между срабатыванием `blur` и установкой значения в input
                const ref = (node: HTMLElement | null) => {
                  if (!node) {
                    return;
                  }

                  const dayNumber = Number.parseInt(`${i}${k}`, 10);

                  dayRefs.current[dayNumber] = node;
                };

                return (
                  <td
                    className={cn(styles["day-cell"], {
                      ...getRangeStatusOfDay(day),
                    })}
                    key={`day-${k}`}
                  >
                    <button
                      type="button"
                      className={cn(styles.day, {
                        [styles.today]: isCurrentDay(day),
                        [styles.disabled]: isDisabledDay(day),
                        [styles.selected]: isSelectedDay(day),
                        [styles.empty]: day === null,
                      })}
                      ref={ref}
                      data-test-id={`${dataTestId}-day-${day}`}
                      onClick={handleSelectDay(day)}
                    >
                      {day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
